import { type ReactNode, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router";
import { v4 } from "uuid";
import { Search } from "lucide-react";
import t from "@/lang/lang";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import {
  useGfsAssessmentGetQuery,
  useGfsAssessmentListQuery,
  useGfsAssessmentPutMutation,
} from "@/services/backend/gfs/assessment/api";
import {
  GfsAssessment,
  GfsAssessmentListItem,
} from "@/services/backend/gfs/assessment/types";
import { useGfsCatalogListQuery } from "@/services/backend/gfs/catalog/api";
import { preferredGfsCatalogId } from "@/services/backend/gfs/catalog/catalog-selection";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { toast } from "sonner";
import {
  MilestonePage,
  MilestonePageActions,
  MilestonePageContent,
  MilestonePageHeader,
  MilestonePageTitle,
} from "@/shared/bsm/core/milestone/components/ui/milestone-page";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";

export function GfsAssessmentListPage() {
  const { processId = "" } = useParams();
  const { data: process, error: processError } = useProcessesProcessShowQuery({
    id: processId,
  });

  if (processError) return <RTKQueryErrorAlert error={processError} />;
  if (!process) return null;
  if (!process.customerId) {
    return <div>{t("Diesem Vorgang ist noch kein Kunde zugeordnet.")}</div>;
  }

  return (
    <AssessmentPage
      companyId={process.companyId}
      customerId={process.customerId}
    />
  );
}

function AssessmentPage({
  companyId,
  customerId,
}: {
  companyId: string;
  customerId: string;
}) {
  const navigate = useNavigate();
  const {
    data: list,
    error,
    isLoading,
  } = useGfsAssessmentListQuery({
    company: { active: true, values: [companyId] },
    customer: { active: true, values: [customerId] },
  });
  const [creating, setCreating] = useState(false);

  return (
    <MilestonePage>
      <MilestonePageHeader>
        <MilestonePageTitle>{t("Bestandsaufnahmen")}</MilestonePageTitle>
        <MilestonePageActions>
          <Button onClick={() => setCreating(true)}>{t("Erstellen")}</Button>
        </MilestonePageActions>
      </MilestonePageHeader>
      <MilestonePageContent>
        {error && <RTKQueryErrorAlert error={error} />}
        <Card>
          <AssessmentHistoryTable
            isLoading={isLoading}
            items={list?.items ?? []}
          />
        </Card>
        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t("Neue Bestandsaufnahme erstellen")}</DialogTitle>
            </DialogHeader>
            <CreateAssessmentForm
              companyId={companyId}
              customerId={customerId}
              assessments={list?.items ?? []}
              onClose={() => setCreating(false)}
              onCreated={(assessmentId) => {
                setCreating(false);
                navigate(assessmentId);
              }}
            />
          </DialogContent>
        </Dialog>
      </MilestonePageContent>
    </MilestonePage>
  );
}

function AssessmentHistoryTable({
  isLoading,
  items,
}: {
  isLoading: boolean;
  items: GfsAssessmentListItem[];
}) {
  if (isLoading) {
    return <AssessmentHistoryTableSkeleton />;
  }

  const sortedItems = [...items].sort(byCreatedAtAndId);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Uhrzeit")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Thema")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedItems.map((assessment) => (
          <TableRow key={assessment.id}>
            <TableCell>{formatDate(assessment.createdAt)}</TableCell>
            <TableCell>{formatTime(assessment.createdAt)}</TableCell>
            <TableCell>
              <NavLink to={`${assessment.id}`}>
                <DocumentNumberById
                  documentId={assessment.id}
                  skip={!assessment.immutable}
                />
              </NavLink>
            </TableCell>
            <TableCell>{assessment.subject}</TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <NavLink to={`${assessment.id}`}>
                  <Button variant="ghost">
                    <Search className="h-4 w-4" />
                  </Button>
                </NavLink>
              </div>
            </TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} className="text-center">
              {t("Keine Bestandsaufnahmen gefunden")}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function byCreatedAtAndId(
  left: { createdAt: string; id: string },
  right: { createdAt: string; id: string },
) {
  const createdAtOrder = right.createdAt.localeCompare(left.createdAt);
  if (createdAtOrder !== 0) return createdAtOrder;
  return right.id.localeCompare(left.id);
}

function AssessmentHistoryTableSkeleton() {
  return (
    <Table className="animate-pulse">
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Vorgang Thema")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
    </Table>
  );
}

function CreateAssessmentForm({
  companyId,
  customerId,
  assessments,
  onClose,
  onCreated,
}: {
  companyId: string;
  customerId: string;
  assessments: {
    id: string;
    catalogId: string;
    subject: string;
    createdAt: string;
  }[];
  onClose: () => void;
  onCreated: (assessmentId: string) => void;
}) {
  const [catalogId, setCatalogId] = useState("");
  const [sourceAssessmentId, setSourceAssessmentId] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const { data: catalogs } = useGfsCatalogListQuery({
    company: { active: true, values: [companyId] },
    limit: 500,
  });
  const {
    data: sourceAssessment,
    error: sourceError,
    isFetching: isLoadingSource,
  } = useGfsAssessmentGetQuery(
    { id: sourceAssessmentId },
    { skip: !sourceAssessmentId },
  );
  const [save, saveState] = useGfsAssessmentPutMutation();
  const availableCatalogs = useMemo(
    () =>
      (catalogs?.items ?? []).filter(
        (catalog) => !catalog.customerId || catalog.customerId === customerId,
      ),
    [catalogs, customerId],
  );

  useEffect(() => {
    if (catalogId || sourceAssessmentId) return;
    const preferredCatalogId = preferredGfsCatalogId(
      availableCatalogs,
      customerId,
    );
    if (preferredCatalogId) setCatalogId(preferredCatalogId);
  }, [availableCatalogs, catalogId, customerId, sourceAssessmentId]);

  useEffect(() => {
    if (!sourceAssessment) return;
    setCatalogId(sourceAssessment.catalogId);
    setSubject(sourceAssessment.subject);
    setDescription(sourceAssessment.description);
  }, [sourceAssessment]);

  const submit = async () => {
    if (!catalogId || isLoadingSource) return;

    const id = v4();
    const now = new Date().toISOString();
    const assessment = createAssessment({
      id,
      companyId,
      customerId,
      catalogId,
      subject,
      description,
      sourceAssessment,
      now,
    });

    try {
      await save({
        id,
        assessment,
      }).unwrap();
      onCreated(id);
    } catch {
      toast.error(t("Fehler"), {
        description: t("Die Bestandsaufnahme konnte nicht gespeichert werden."),
      });
    }
  };

  return (
    <div className="space-y-4">
      {saveState.error && <RTKQueryErrorAlert error={saveState.error} />}
      {sourceError && <RTKQueryErrorAlert error={sourceError} />}
      <Field label={t("Katalog für die Bestandsaufnahme")}>
        <Select value={catalogId} onValueChange={setCatalogId}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("Katalog auswählen")} />
          </SelectTrigger>
          <SelectContent>
            {availableCatalogs.map((catalog) => (
              <SelectItem key={catalog.id} value={catalog.id}>
                {catalog.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label={t("Bestehende Bestandsaufnahme als Vorlage")}>
        <Select
          value={sourceAssessmentId || "empty"}
          onValueChange={(value) => {
            const nextSourceID = value === "empty" ? "" : value;
            setSourceAssessmentId(nextSourceID);
            if (!nextSourceID) {
              setSubject("");
              setDescription("");
            }
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("Neue leere Bestandsaufnahme")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="empty">
              {t("Neue leere Bestandsaufnahme")}
            </SelectItem>
            {assessments.map((assessment) => (
              <SelectItem key={assessment.id} value={assessment.id}>
                {assessment.subject || t("Ohne Betreff")} ·{" "}
                {new Date(assessment.createdAt).toLocaleDateString()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-sm">
          {t(
            "Optional. Die Angaben sowie Gebäude und Bereiche der Vorlage werden kopiert.",
          )}
        </p>
      </Field>

      <Field label={t("Betreff")}>
        <Input
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
        />
      </Field>

      <Field label={t("Beschreibung")}>
        <Textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          {t("Abbrechen")}
        </Button>
        <Button
          disabled={
            !catalogId ||
            isLoadingSource ||
            !!sourceError ||
            (!!sourceAssessmentId && !sourceAssessment) ||
            saveState.isLoading
          }
          onClick={submit}
        >
          {sourceAssessmentId
            ? t("Bestandsaufnahme kopieren")
            : t("Bestandsaufnahme erstellen")}
        </Button>
      </div>
    </div>
  );
}

function createAssessment({
  id,
  companyId,
  customerId,
  catalogId,
  subject,
  description,
  sourceAssessment,
  now,
}: {
  id: string;
  companyId: string;
  customerId: string;
  catalogId: string;
  subject: string;
  description: string;
  sourceAssessment?: GfsAssessment;
  now: string;
}): GfsAssessment {
  if (sourceAssessment) {
    const { buildings, areaIDs } = copyBuildingSnapshots(
      sourceAssessment.buildings,
      now,
    );
    return {
      ...sourceAssessment,
      id,
      companyId,
      customerId,
      catalogId,
      subject,
      description,
      workEstimate: normalizeGfsWorkEstimate(sourceAssessment.workEstimate),
      immutable: false,
      buildings,
      areaAssessments: sourceAssessment.areaAssessments.map(
        (areaAssessment) => ({
          ...areaAssessment,
          areaId: areaIDs.get(areaAssessment.areaId) ?? areaAssessment.areaId,
          laborDuration: areaAssessment.laborDuration ?? 0,
          areaSizeM2: areaAssessment.areaSizeM2 ?? 0,
        }),
      ),
      createdAt: now,
      updatedAt: now,
    };
  }

  return {
    id,
    companyId,
    customerId,
    catalogId,
    subject,
    description,
    locationNotes: "",
    infestationLevel: "",
    workEstimate: {
      technicians: 0,
      duration: 0,
      travelDuration: 0,
      trips: 0,
      measureCount: 0,
      measurePeriodValue: 0,
      measurePeriodUnit: "",
      eradicationMonitoring: false,
      eradicationMonitoringDuration: 0,
    },
    specialConditions: [],
    customerDuties: [],
    pestCategories: [],
    pestGroups: [],
    treatmentMethods: [],
    materials: [],
    buildings: [],
    areaAssessments: [],
    immutable: false,
    createdAt: now,
    updatedAt: now,
  };
}

function normalizeGfsWorkEstimate(
  workEstimate: GfsAssessment["workEstimate"],
): GfsAssessment["workEstimate"] {
  return {
    technicians: workEstimate.technicians ?? 0,
    duration: workEstimate.duration ?? 0,
    travelDuration: workEstimate.travelDuration ?? 0,
    trips: workEstimate.trips ?? 0,
    measureCount: workEstimate.measureCount ?? 0,
    measurePeriodValue: workEstimate.measurePeriodValue ?? 0,
    measurePeriodUnit: workEstimate.measurePeriodUnit ?? "",
    eradicationMonitoring: workEstimate.eradicationMonitoring ?? false,
    eradicationMonitoringDuration:
      workEstimate.eradicationMonitoringDuration ?? 0,
  };
}

function copyBuildingSnapshots(
  buildings: GfsAssessment["buildings"],
  takenAt: string,
): {
  buildings: GfsAssessment["buildings"];
  areaIDs: Map<string, string>;
} {
  const areaIDs = new Map<string, string>();
  const copiedBuildings = buildings.map((building) => {
    const levelIDs = new Map(
      building.levels.map((level) => [level.id, v4()] as const),
    );
    const copiedAreas = building.areas.map((area) => {
      const id = v4();
      areaIDs.set(area.id, id);
      return {
        ...area,
        id,
        levelId: area.levelId ? levelIDs.get(area.levelId) : undefined,
      };
    });
    return {
      ...building,
      id: v4(),
      levels: building.levels.map((level) => ({
        ...level,
        id: levelIDs.get(level.id) ?? v4(),
      })),
      areas: copiedAreas,
      takenAt,
    };
  });
  return { buildings: copiedBuildings, areaIDs };
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
